/**
 * Brand constants + pillar definitions (v1).
 *
 * Rotating brand hero (outcomes):
 *   "Your Business" + {Always On, Always Answering, Always Growing}
 *
 * Pillars (delivery mechanisms):
 *   1. Simple Setup     — forward one phone number, AI receptionist answers every call
 *   2. AI Business Page — live page structured for AI assistants
 *
 * Imported by landing pages, homepage sections, navbar, pricing, OG images,
 * and the marketing agents.
 */

export interface PillarDef {
  readonly name: string;
  readonly masterTagline: string;
  readonly campaignTagline: string;
  readonly heroTriplet: readonly [string, string, string];
  readonly painStat: string;
  readonly benefits: readonly [string, string, string];
  readonly hashtags: readonly string[];
  readonly landingUrl: string;
  readonly landingPath: string;
  readonly accent: 'cyan' | 'gold';
  readonly accentHex: string;
  readonly paidAdPolicy: string;
}

export const BRAND = {
  umbrella: 'Your Business. Always On. Always Answering. Always Growing.',
  rotatingHero: ['Always On', 'Always Answering', 'Always Growing'] as const,
  rotatingHeroPrefix: 'Your Business',
  categoryName: 'AI Business Reception',
  categoryClaim: 'BizRnR is the AI Business Reception platform.',
  homepageSubheadline:
    'Your AI receptionist answers every call, 24/7.',
  /** Punchy lede — sets the promise above the fold. */
  homepageSub:
    'Bri, your AI receptionist, answers missed calls, qualifies callers, and texts you the next step before the lead goes cold.',
  /** Primary value proposition — single sentence, answer-first. */
  homepageDual:
    'Start with AI Receptionist for $99/mo. Manual setup is waived and white-glove onboarding is included, so forwarding your number is the only hard part.',
  /** AI-grid discovery angle — moved below the fold for the Code Red Spark hero. */
  homepageAiGrid:
    'Plus a smart business page below the fold that helps new customers find you in ChatGPT, Gemini, Claude, and Perplexity.',
  primaryCta: 'AI Receptionist is $99/mo. Cancel anytime. 30-day money-back guarantee.',
  primaryCtaShort: '$99/mo · Cancel anytime · 30-day guarantee',
  /** Canonical primary CTA label across every marketing page. */
  primaryCtaLabel: 'Get started',
  /** Canonical primary CTA href. UTM-tagged variants compose from this. */
  primaryCtaHref: '/auth/register',
  /**
   * Canonical setup-time claim for the homepage + every supporting page.
   * Wave 6.A (2026-04-25) — flipped from "30 seconds" → "60 seconds" to
   * align with the homepage `HowItWorksSection` "deploy an AI voice agent
   * in 60 seconds." headline. Every supporting page interpolates this
   * constant — never hardcode the number elsewhere.
   */
  setupTimeClaim: '60 seconds',
  /** Canonical AI-assistants list (homepage AI-grid order, Oxford-comma form). */
  aiAssistantsList: 'ChatGPT, Gemini, Claude, and Perplexity',
  /** Same list without Oxford comma — for inline prose where the comma reads heavy. */
  aiAssistantsListShort: 'ChatGPT, Gemini, and Perplexity',
  /** Canonical AI-recommendation proof stat (matches `V4_BRAND.homepageAiGrid`). */
  aiRecommendStat:
    '58% of customers now ask ChatGPT, Gemini, and Perplexity for local business recommendations.',
  /** Canonical missed-call pain stat (matches `PILLAR_1_SIMPLE_SETUP.painStat`). */
  missedCallStat:
    '50–70% of small-business inbound calls go unanswered — most callers never leave a message',
  /** Canonical dental no-show reduction stat (Bright Smile Dental case study: 22% → 12%). */
  dentalNoShowReduction: '45%',
  primaryVerticals:
    'HVAC, dental, plumbing, real estate, legal, insurance, solar, restoration, medspa, auto repair, roofing, landscaping, electrical, pest control, locksmith, chiropractic, veterinary, accounting, general contractor, cleaning',
  /** Canonical human receptionist cost comparison range. */
  humanReceptionistCost: '$2,500–$4,000/month',
} as const;

export const PILLAR_1_SIMPLE_SETUP: PillarDef = {
  name: 'Simple Setup',
  masterTagline: 'Simple Setup. Your business, always on.',
  campaignTagline: 'Forward one number. Your business never closes.',
  heroTriplet: [
    'Forward your number.',
    'AI answers every call.',
    'Your business is always on.',
  ],
  painStat:
    '50–70% of small-business inbound calls go unanswered — most callers never leave a message',
  benefits: [
    'Every call answered on the first ring, 24/7/365',
    'Every lead qualified and booked on your calendar in the same call',
    'Every existing customer reachable any time, anywhere',
  ],
  hashtags: [
    '#SimpleSetup',
    '#AlwaysOn',
    '#NeverMissACall',
    '#AIReceptionist',
    '#SmallBusinessAI',
  ],
  landingUrl: 'https://bizrnr.com/simple-setup',
  landingPath: '/simple-setup',
  accent: 'cyan',
  accentHex: '#06b6d4',
  paidAdPolicy:
    'organic-only — paid platforms scrutinize "double" revenue claims',
};

export const PILLAR_2_AI_BUSINESS_PAGE: PillarDef = {
  name: 'AI Business Page',
  masterTagline: 'AI Business Page. Your business, always answering.',
  campaignTagline: 'When they ask AI, your business is ready.',
  heroTriplet: [
    'Sign up.',
    'Get seen.',
    'Your business is always answering.',
  ],
  painStat:
    '58% of consumers ask AI for local business recommendations — up from 8% two years ago',
  benefits: [
    'Your AI Business Page goes live the moment you sign up',
    'Voice widget + chat widget close AI-discovered visitors on-page',
    'Structured for ChatGPT, Gemini, Claude, Perplexity, Google AI Overviews, Bing Chat',
  ],
  hashtags: [
    '#AIBusinessPage',
    '#AlwaysAnswering',
    '#FoundByAI',
    '#AIVisibility',
    '#AEO',
  ],
  landingUrl: 'https://bizrnr.com/ai-business-page',
  landingPath: '/ai-business-page',
  accent: 'gold',
  accentHex: '#fbbf24',
  paidAdPolicy: 'paid-eligible — factual capability claim',
};

export const PILLARS = [PILLAR_1_SIMPLE_SETUP, PILLAR_2_AI_BUSINESS_PAGE] as const;

export const CATEGORY_CONTRASTS = {
  answeringService:
    'Answering services cost $2,500–$4,000/month and still miss calls. BizRnR costs $99 and misses none.',
  virtualReceptionist:
    'Virtual receptionists forward calls. BizRnR qualifies, books, and logs in one conversation.',
  seoTool:
    "SEO tools optimize for Google. BizRnR adds AI-readable business context for assistants that increasingly influence discovery.",
  googleBusinessProfile: 'GBP gets you on Google. BizRnR makes you AI-discoverable.',
} as const;

export const AI_SEARCH_PLATFORMS = [
  'ChatGPT',
  'Gemini',
  'Claude',
  'Perplexity',
  'Google AI Overviews',
  'Bing Chat',
] as const;
